// Черновата на листа „Изписване" — общото `useWorksheetDraft` със снимката на
// изписването: флагът „незапазени промени", autosave-ът (браузър + сървър) при
// ново изписване и нещата за футъра („Изтрий черновата" · „Запази временно").
// Коя чернова (ако има) продължава листът, решава страницата
// (`WriteoffCreatePage` → `draft`), преди да го монтира. Листът подава
// състоянието си лениво (`sheet`) — редовете идват от hook-а по-долу в тялото.
import type { TFunction } from "i18next";
import dayjs, { type Dayjs } from "dayjs";
import type { Product } from "../../../../api/inventory";
import { useAppSelector } from "../../../../store/hooks";
import type { DraftFooterProps } from "../../components/drafts/DraftWorksheetParts";
import { useWorksheetDraft } from "../../components/drafts/useWorksheetDraft";
import { writeoffPreviewItems } from "./useWriteoffLeave";
import { WRITEOFF_DRAFT_STORE, writeoffDraftSummary, type WriteoffDraft } from "./writeoffDraft";
import type { WriteoffKind, WriteoffLineDraft } from "./writeoffWorksheetShared";

// Сървърната чернова, от която листът тръгва (своя или поета от колега)
export type WriteoffSheetDraft = { draftId: number | null; own: boolean };

// Състоянието на листа в момента на снимката
type SheetState = {
  date: Dayjs | null;
  warehouseId: number | null;
  warehouseName: string | null;
  kind: WriteoffKind;
  reason: string;
  comment: string;
  lines: WriteoffLineDraft[];
  totalGross: number;
};

type Params = {
  mode: "new" | "edit";
  homePath: string;
  draft?: WriteoffSheetDraft;
  sheet: () => SheetState;
};

export const useWriteoffDraft = ({ mode, homePath, draft, sheet }: Params) => {
  const authNames = useAppSelector((s) => s.auth.names);
  const worksheetDraft = useWorksheetDraft<WriteoffDraft>({
    type: "writeoff",
    mode,
    homePath,
    routerState: draft?.draftId != null ? { draftId: draft.draftId, ownDraft: draft.own } : null,
    local: WRITEOFF_DRAFT_STORE,
    snapshot: () => {
      const state = sheet();
      return {
        payload: {
          v: 1,
          savedAt: new Date().toISOString(),
          savedBy: authNames || null,
          date: (state.date ?? dayjs()).toISOString(),
          warehouseId: state.warehouseId,
          kind: state.kind,
          reason: state.reason,
          comment: state.comment,
          lines: state.lines,
        },
        summary: writeoffDraftSummary({
          warehouseName: state.warehouseName,
          reason: state.reason,
          kind: state.kind,
          linesCount: state.lines.length,
          total: state.totalGross,
        }),
      };
    },
  });

  // „Изтрий черновата" · „Запази временно" за футъра
  const footer = (t: TFunction, productById: Map<number, Product>): DraftFooterProps => ({
    canDeleteDraft: worksheetDraft.sync.draftId != null || sheet().lines.length > 0,
    describeDraft: () => ({ label: null, items: writeoffPreviewItems(t, sheet().lines, productById) }),
    onDeleteDraft: worksheetDraft.deleteDraft,
    onSaveTemp: worksheetDraft.saveTemp,
  });

  return { ...worksheetDraft, footer };
};
